<template>
  <div class="app-toast-container">
    <transition-group name="app-toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="app-toast-item"
        :class="`is-${toast.type}`"
      >
        {{ toast.message }}
      </div>
    </transition-group>
  </div>
</template>

<script>
/** 전역 토스트 이벤트 이름 — window.dispatchEvent(new CustomEvent('app:toast', { detail: { message, type } })) 로 발행 */
const TOAST_EVENT_NAME = 'app:toast'

/**
 * @component AppToast
 * @description 전역 토스트 알림 컴포넌트.
 * window 커스텀 이벤트('app:toast')를 수신하여 화면 상단 중앙(모바일 <768px 은 하단 중앙)에 알림을 표시합니다.
 * type은 'success' | 'error' | 'warning' | 'info' 중 하나입니다.
 */
export default {
  // ── 반응형 데이터 ──
  data() {
    return {
      /** 현재 화면에 표시 중인 토스트 목록 */
      toasts: [],
      /** 다음 토스트에 부여할 고유 ID */
      nextToastId: 1
    }
  },
  mounted() {
    window.addEventListener(TOAST_EVENT_NAME, this.handleToastEvent)
  },
  beforeUnmount() {
    window.removeEventListener(TOAST_EVENT_NAME, this.handleToastEvent)
  },
  // ── 메서드 ──
  methods: {
    /**
     * 'app:toast' 커스텀 이벤트를 처리하여 토스트를 추가하고 자동 제거 타이머를 설정합니다.
     * @param {CustomEvent} event - detail에 { message, type, duration } 을 포함한 이벤트 객체
     */
    handleToastEvent(event) {
      const payload = event?.detail || {}
      const message = payload.message ? String(payload.message) : ''
      if (!message) {
        return
      }

      const toast = {
        id: this.nextToastId++,
        message,
        type: payload.type || 'info'
      }
      this.toasts.push(toast)

      const duration = Number.isFinite(payload.duration) ? payload.duration : 2500
      window.setTimeout(() => {
        this.removeToast(toast.id)
      }, Math.max(duration, 800))
    },
    /**
     * 지정한 ID의 토스트를 목록에서 제거합니다.
     * @param {number} id - 제거할 토스트의 고유 ID
     */
    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id)
    }
  }
}
</script>

<style scoped>
/* --- 토스트 컨테이너 (화면 상단 중앙 고정) --- */
.app-toast-container {
  position: fixed;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast, 400);
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 8px);
  align-items: center;
  pointer-events: none;
}

/* --- 토스트 아이템 기본 스타일 --- */
.app-toast-item {
  min-width: 264px;
  max-width: 504px;
  padding: var(--space-3, 12px) var(--space-5, 20px);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-xl, 0 8px 24px rgba(0, 0, 0, 0.15));
  color: #fff;
  font-size: var(--font-body-md, 0.875rem);
  font-weight: var(--font-weight-semibold, 600);
  line-height: 1.45;
  text-align: center;
}

.app-toast-item.is-success {
  background: var(--color-success, #2E7D5B);
}

.app-toast-item.is-error {
  background: var(--color-danger, #C03A3A);
}

.app-toast-item.is-warning {
  background: var(--color-warning, #C8870A);
}

.app-toast-item.is-info {
  background: var(--color-info, #3670B3);
}

/* --- 모바일: 제목·본문 위를 가리지 않게 하단 중앙(플로팅 버튼 위)으로 (SURINP-777) --- */
@media (max-width: 767px) {
  .app-toast-container {
    top: auto;
    bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    width: calc(100vw - 32px);
  }

  .app-toast-item {
    min-width: 0;
    max-width: 100%;
  }
}

/* --- 등장/퇴장 트랜지션 --- */
.app-toast-enter-active,
.app-toast-leave-active {
  transition: all var(--transition-normal, 0.2s ease);
}

.app-toast-enter-from,
.app-toast-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
}
</style>
